import React from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  headerText: string;
  warningText?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  headerText,
  warningText,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  isLoading = false,
  variant = 'warning',
}) => {
  const variantStyles = {
    danger: {
      icon: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      button: 'danger',
    },
    warning: {
      icon: 'text-yellow-600',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      button: 'primary',
    },
    info: {
      icon: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      button: 'primary',
    },
  };

  const style = variantStyles[variant];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      closeOnBackdrop={!isLoading}
    >
      <div className="flex flex-col gap-6">
        {/* Icon and Header Text */}
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 ${style.icon}`}>
            <AlertTriangle size={24} />
          </div>
          <div className="flex-1">
            <p className="text-base text-gray-900">{headerText}</p>
          </div>
        </div>

        {/* Warning Text */}
        {warningText && (
          <div className={`p-4 rounded-lg ${style.bg} border ${style.border}`}>
            <p className="text-sm text-gray-700">{warningText}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelButtonText}
          </Button>
          <Button
            variant={style.button as 'primary'}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : confirmButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
